// src/components/Inquiry/DraftEmailPreview.tsx
//
// The email preview while an inquiry is being edited: rebuilt from what is in
// the form right now, not read back from what is stored.
//
// The saved inquiry's preview is fetched by id (InquiryDialog's own
// EmailPreview), which is right for reading one - what matters there is what
// the supplier actually received. It is wrong the moment the form beside it is
// open: every change to the message, the deadline, the supplier or the item
// lines is a change to the email, and previewing the stored version shows the
// one thing the editor is not looking at. So editing borrows the create form's
// preview instead - the same POST /render/html from the same payload builder -
// with the inquiry's id in the payload so it renders as itself rather than as
// a new one.
import { FunctionComponent, useContext } from "react";
import AppStateContext from "contexts/AppStateContext";
import { CartType } from "types/CartTypes";
import { OrderType } from "types/OrderTypes";
import { CreateInquiryItemType } from "types/InquiryTypes";
import { usePartners } from "hooks/usePartners";
import { InquiryFormValues } from "./types";
import { useEmailPreview } from "./hooks/useEmailPreview";
import EmailPreviewPanel from "./EmailPreviewPanel";

/** What the edit form has in hand, and all the preview needs from it. */
export type InquiryDraft = {
  values: InquiryFormValues;
  mergedItems: CreateInquiryItemType[];
  prodObj: OrderType | CartType | undefined;
  prodType: "order" | "cart";
};

const DraftEmailPreview: FunctionComponent<{
  draft: InquiryDraft;
  /** Rendered as this inquiry rather than as a new one. */
  inquiryId: number;
}> = ({ draft, inquiryId }) => {
  const { token } = useContext(AppStateContext);
  // The preview needs the whole partner, not the id the form holds.
  const partners = usePartners() ?? [];
  const partner = partners.find((p) => p.id === draft.values.partnerId);

  const { html, isOpen, downloadPDF } = useEmailPreview({
    formValues: draft.values,
    mergedItems: draft.mergedItems,
    partner,
    prodObj: draft.prodObj,
    prodType: draft.prodType,
    oldInquiryId: inquiryId,
    token,
  });

  return (
    <EmailPreviewPanel
      html={html}
      isOpen={isOpen}
      downloadPDF={downloadPDF}
    />
  );
};

export default DraftEmailPreview;
